/**
 * OutlookCalendar - World-class Outlook-style calendar
 * 
 * Features:
 * - Day, Week, Month views with proper event spanning
 * - Overlapping events shown side-by-side
 * - Microsoft Outlook sync via Graph API
 * - Teams meeting creation
 * - Drag and drop (future)
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon,
  Video, MapPin, Users, Clock, X, Loader2, RefreshCw,
  ExternalLink, Check, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../utils/auth';
import { useTranslation } from '../../context/LanguageContext';
import { 
  format, addDays, addWeeks, addMonths, startOfWeek, endOfWeek, 
  startOfMonth, endOfMonth, isSameDay, isSameMonth, parseISO, 
  differenceInMinutes, isToday, addHours, startOfDay, endOfDay,
  getHours, getMinutes, areIntervalsOverlapping
} from 'date-fns';
import { nb, enUS } from 'date-fns/locale';

// i18n translations for calendar
const calendarTranslations = {
  en: {
    title: 'Calendar',
    today: 'Today',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    newEvent: 'New Event',
    createEvent: 'Create Event',
    cancel: 'Cancel',
    create: 'Create',
    creating: 'Creating...',
    sync: 'Sync',
    syncing: 'Syncing...',
    outlookConnected: 'Outlook connected',
    outlookNotConnected: 'Outlook not connected',
    connectOutlook: 'Connect',
    noEvents: 'No events',
    loading: 'Loading...',
    eventTitle: 'Title',
    eventTitlePlaceholder: 'Meeting title',
    date: 'Date',
    startTime: 'Start time',
    endTime: 'End time',
    location: 'Location',
    locationPlaceholder: 'Add location',
    description: 'Description',
    descriptionPlaceholder: 'Add description',
    attendees: 'Attendees',
    addAttendee: 'Add attendee',
    attendeePlaceholder: 'Enter email address',
    teamsMeeting: 'Teams meeting',
    addTeamsLink: 'Add Teams link',
    joinMeeting: 'Join meeting',
    viewDetails: 'View details',
    organizer: 'Organizer',
    eventCreated: 'Event created',
    eventCreateError: 'Could not create event',
    syncSuccess: 'Calendar synced',
    syncError: 'Could not sync calendar',
    allDay: 'All day',
    deadline: 'Deadline',
    focusTime: 'Focus time',
    blocked: 'Blocked'
  },
  nb: {
    title: 'Kalender',
    today: 'I dag',
    day: 'Dag',
    week: 'Uke',
    month: 'Måned',
    newEvent: 'Ny hendelse',
    createEvent: 'Opprett hendelse',
    cancel: 'Avbryt',
    create: 'Opprett',
    creating: 'Oppretter...',
    sync: 'Synkroniser',
    syncing: 'Synkroniserer...',
    outlookConnected: 'Outlook tilkoblet',
    outlookNotConnected: 'Outlook ikke tilkoblet',
    connectOutlook: 'Koble til',
    noEvents: 'Ingen hendelser',
    loading: 'Laster...',
    eventTitle: 'Tittel',
    eventTitlePlaceholder: 'Møtetittel',
    date: 'Dato',
    startTime: 'Starttid',
    endTime: 'Sluttid',
    location: 'Sted',
    locationPlaceholder: 'Legg til sted',
    description: 'Beskrivelse',
    descriptionPlaceholder: 'Legg til beskrivelse',
    attendees: 'Deltakere',
    addAttendee: 'Legg til deltaker',
    attendeePlaceholder: 'Skriv inn e-postadresse',
    teamsMeeting: 'Teams-møte',
    addTeamsLink: 'Legg til Teams-lenke',
    joinMeeting: 'Bli med i møtet',
    viewDetails: 'Vis detaljer',
    organizer: 'Arrangør',
    eventCreated: 'Hendelse opprettet',
    eventCreateError: 'Kunne ikke opprette hendelse',
    syncSuccess: 'Kalender synkronisert',
    syncError: 'Kunne ikke synkronisere kalender',
    allDay: 'Hele dagen',
    deadline: 'Frist',
    focusTime: 'Fokustid',
    blocked: 'Blokkert'
  }
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Types
interface CalendarEvent {
  id: string;
  subject: string;
  body?: string;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  location?: string;
  attendees?: Array<{ email: string; name: string; status?: string }>;
  organizer?: { address: string; name: string };
  is_online_meeting?: boolean;
  online_meeting_url?: string;
  teams_meeting?: { joinUrl?: string };
  event_type?: string;
  case_id?: string;
  case_title?: string;
  // Computed fields for layout
  column?: number;
  totalColumns?: number;
}

interface Attendee {
  email: string;
  name?: string;
}

type ViewType = 'day' | 'week' | 'month';

interface OutlookCalendarProps {
  onNavigateToCase?: (caseId: string) => void;
}

interface MeetingInfo {
  meetingId?: string;
  passcode?: string;
  dialIn?: string;
}

// Helper: Extract Teams link from body HTML
const extractTeamsLink = (body?: string): string | undefined => {
  if (!body) return undefined;
  const match = body.match(/https:\/\/teams\.microsoft\.com\/[^\s"<]+/);
  return match ? match[0] : undefined;
};

// Helper: Extract meeting info from Teams body HTML
const extractMeetingInfo = (body?: string): MeetingInfo | null => {
  if (!body) return null;
  
  const info: MeetingInfo = {};
  
  // Extract Meeting ID
  const meetingIdMatch = body.match(/Meeting ID[:\s]*([0-9\s]+)/i);
  if (meetingIdMatch) {
    info.meetingId = meetingIdMatch[1].trim();
  }
  
  // Extract Passcode
  const passcodeMatch = body.match(/Passcode[:\s]*([A-Za-z0-9]+)/i);
  if (passcodeMatch) {
    info.passcode = passcodeMatch[1].trim();
  }
  
  // Extract dial-in number
  const dialInMatch = body.match(/(\+\d{1,3}[\s\d]+)/);
  if (dialInMatch) {
    info.dialIn = dialInMatch[1].trim();
  }
  
  // Only return if we found something
  if (info.meetingId || info.passcode || info.dialIn) {
    return info;
  }
  
  return null;
};

// Constants
const HOUR_HEIGHT = 60; // pixels per hour
const START_HOUR = 7;
const END_HOUR = 20;
const TIME_SLOTS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);

// Helper: Calculate event position and height
const getEventStyle = (event: CalendarEvent, dayStart: Date) => {
  const eventStart = parseISO(event.start.dateTime);
  const eventEnd = parseISO(event.end.dateTime);
  
  // Calculate minutes from day start (START_HOUR)
  const startMinutes = (getHours(eventStart) - START_HOUR) * 60 + getMinutes(eventStart);
  const endMinutes = (getHours(eventEnd) - START_HOUR) * 60 + getMinutes(eventEnd);
  
  // Clamp to visible range
  const clampedStart = Math.max(0, startMinutes);
  const clampedEnd = Math.min((END_HOUR - START_HOUR) * 60, endMinutes);
  
  const top = (clampedStart / 60) * HOUR_HEIGHT;
  const height = Math.max(20, ((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT);
  
  return { top, height };
};

// Helper: Find overlapping events and assign columns
const assignEventColumns = (events: CalendarEvent[]): CalendarEvent[] => {
  if (events.length === 0) return [];
  
  // Sort by start time
  const sorted = [...events].sort((a, b) => 
    parseISO(a.start.dateTime).getTime() - parseISO(b.start.dateTime).getTime()
  );
  
  // Find overlapping groups
  const groups: CalendarEvent[][] = [];
  let currentGroup: CalendarEvent[] = [];
  
  sorted.forEach((event, idx) => {
    if (currentGroup.length === 0) {
      currentGroup.push(event);
    } else {
      // Check if this event overlaps with any event in current group
      const overlaps = currentGroup.some(groupEvent => {
        const eventStart = parseISO(event.start.dateTime);
        const eventEnd = parseISO(event.end.dateTime);
        const groupStart = parseISO(groupEvent.start.dateTime);
        const groupEnd = parseISO(groupEvent.end.dateTime);
        
        return areIntervalsOverlapping(
          { start: eventStart, end: eventEnd },
          { start: groupStart, end: groupEnd }
        );
      });
      
      if (overlaps) {
        currentGroup.push(event);
      } else {
        groups.push([...currentGroup]);
        currentGroup = [event];
      }
    }
  });
  
  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }
  
  // Assign columns within each group
  const result: CalendarEvent[] = [];
  
  groups.forEach(group => {
    const columns: CalendarEvent[][] = [];
    
    group.forEach(event => {
      const eventStart = parseISO(event.start.dateTime);
      const eventEnd = parseISO(event.end.dateTime);
      
      // Find first column where event fits
      let columnIndex = 0;
      let placed = false;
      
      while (!placed) {
        if (!columns[columnIndex]) {
          columns[columnIndex] = [];
        }
        
        // Check if event overlaps with any event in this column
        const hasOverlap = columns[columnIndex].some(colEvent => {
          const colStart = parseISO(colEvent.start.dateTime);
          const colEnd = parseISO(colEvent.end.dateTime);
          return areIntervalsOverlapping(
            { start: eventStart, end: eventEnd },
            { start: colStart, end: colEnd }
          );
        });
        
        if (!hasOverlap) {
          columns[columnIndex].push(event);
          event.column = columnIndex;
          placed = true;
        } else {
          columnIndex++;
        }
      }
    });
    
    // Set total columns for each event in this group
    group.forEach(event => {
      event.totalColumns = columns.length;
      result.push(event);
    });
  });
  
  return result;
};

// Event colors
const getEventColors = (event: CalendarEvent) => {
  if (event.subject?.toLowerCase().includes('cancelled')) {
    return {
      bg: 'bg-gray-100',
      border: 'border-gray-300',
      text: 'text-gray-500',
      accent: 'bg-gray-400'
    };
  }
  if (event.event_type === 'DEADLINE') {
    return {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      accent: 'bg-red-500'
    };
  }
  if (event.event_type === 'TASK') {
    return {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      accent: 'bg-amber-500'
    };
  }
  if (event.is_online_meeting) {
    return {
      bg: 'bg-violet-50',
      border: 'border-violet-200',
      text: 'text-violet-800',
      accent: 'bg-violet-500'
    };
  }
  return {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    accent: 'bg-blue-500'
  };
};

const OutlookCalendar: React.FC<OutlookCalendarProps> = ({ onNavigateToCase }) => {
  const user = auth.getUser();
  
  // i18n
  const { language } = useTranslation();
  const t = calendarTranslations[language as keyof typeof calendarTranslations] || calendarTranslations.en;
  const dateLocale = language === 'nb' ? nb : enUS;
  
  // State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewType>('week');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookChecked, setOutlookChecked] = useState(false);
  
  // Modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // New event form
  const [newEvent, setNewEvent] = useState({
    subject: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    startTime: format(addHours(new Date(), 1), 'HH:00'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    endTime: format(addHours(new Date(), 2), 'HH:00'),
    location: '',
    body: '',
    isTeamsMeeting: false,
    attendees: [] as Attendee[]
  });
  const [attendeeInput, setAttendeeInput] = useState('');
  const [creating, setCreating] = useState(false);

  // Check Outlook connection first
  useEffect(() => {
    const init = async () => {
      await checkOutlookConnection();
      setOutlookChecked(true);
    };
    init();
  }, []);

  // Fetch events after outlook check
  useEffect(() => {
    if (outlookChecked) {
      fetchEvents();
    }
  }, [currentDate, view, outlookChecked, outlookConnected]);

  const checkOutlookConnection = async () => {
    try {
      const response = await axios.get(`${API}/outlook/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setOutlookConnected(response.data.connected);
    } catch (error) {
      setOutlookConnected(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      let startDate: Date, endDate: Date;
      if (view === 'day') {
        startDate = startOfDay(currentDate);
        endDate = endOfDay(currentDate);
      } else if (view === 'week') {
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      }
      
      const results: CalendarEvent[] = [];
      
      if (outlookConnected) {
        try {
          const outlookResponse = await axios.get(`${API}/graph/calendar/events`, {
            params: {
              start_datetime: startDate.toISOString(),
              end_datetime: endDate.toISOString(),
              top: 100
            },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          if (outlookResponse.data.events) {
            results.push(...outlookResponse.data.events.map((e: any) => ({
              ...e,
              start: e.start || { dateTime: e.start_datetime, timeZone: 'Europe/Oslo' },
              end: e.end || { dateTime: e.end_datetime, timeZone: 'Europe/Oslo' }
            })));
          }
        } catch (error) {
          console.error('Error fetching Outlook events:', error);
        }
      }
      
      setEvents(results);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Kunne ikke laste kalender');
    } finally {
      setLoading(false);
    }
  };

  const syncOutlook = async () => {
    setSyncing(true);
    await fetchEvents();
    setSyncing(false);
    toast.success(t.syncSuccess);
  };

  const createEvent = async () => {
    if (!newEvent.subject.trim()) {
      toast.error('Tittel er påkrevd');
      return;
    }
    
    setCreating(true);
    try {
      const startDateTime = `${newEvent.startDate}T${newEvent.startTime}:00`;
      const endDateTime = `${newEvent.endDate}T${newEvent.endTime}:00`;
      
      await axios.post(`${API}/graph/calendar/events`, {
        subject: newEvent.subject,
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        body: newEvent.body || undefined,
        location: newEvent.location || undefined,
        is_online_meeting: newEvent.isTeamsMeeting,
        attendees: newEvent.attendees.length > 0 ? newEvent.attendees : undefined,
        time_zone: 'Europe/Oslo'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(newEvent.isTeamsMeeting ? 'Teams meeting created!' : 'Event created!');
      
      setNewEvent({
        subject: '',
        startDate: format(new Date(), 'yyyy-MM-dd'),
        startTime: format(addHours(new Date(), 1), 'HH:00'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
        endTime: format(addHours(new Date(), 2), 'HH:00'),
        location: '',
        body: '',
        isTeamsMeeting: false,
        attendees: []
      });
      setShowCreateModal(false);
      fetchEvents();
    } catch (error: any) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette hendelse');
    } finally {
      setCreating(false);
    }
  };

  const addAttendee = () => {
    if (attendeeInput.trim() && attendeeInput.includes('@')) {
      setNewEvent({
        ...newEvent,
        attendees: [...newEvent.attendees, { email: attendeeInput.trim() }]
      });
      setAttendeeInput('');
    }
  };

  const removeAttendee = (email: string) => {
    setNewEvent({
      ...newEvent,
      attendees: newEvent.attendees.filter(a => a.email !== email)
    });
  };

  // Navigation
  const goToToday = () => setCurrentDate(new Date());
  
  const goBack = () => {
    if (view === 'day') setCurrentDate(addDays(currentDate, -1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, -1));
    else setCurrentDate(addMonths(currentDate, -1));
  };
  
  const goForward = () => {
    if (view === 'day') setCurrentDate(addDays(currentDate, 1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addMonths(currentDate, 1));
  };

  const getViewTitle = () => {
    if (view === 'day') {
      return format(currentDate, 'EEEE d. MMMM yyyy', { locale: dateLocale });
    } else if (view === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(weekStart, 'd. MMM', { locale: dateLocale })} - ${format(weekEnd, 'd. MMM yyyy', { locale: dateLocale })}`;
    } else {
      return format(currentDate, 'MMMM yyyy', { locale: dateLocale });
    }
  };

  // Get events for a specific day with column assignments
  const getEventsForDay = (date: Date): CalendarEvent[] => {
    const dayEvents = events.filter(event => {
      const eventStart = parseISO(event.start.dateTime);
      return isSameDay(eventStart, date);
    });
    return assignEventColumns(dayEvents);
  };

  // Render time column
  const renderTimeColumn = () => (
    <div className="w-16 flex-shrink-0 border-r border-gray-200 bg-gray-50">
      {TIME_SLOTS.map(hour => (
        <div 
          key={hour} 
          className="h-[60px] border-b border-gray-100 text-right pr-2 pt-0"
        >
          <span className="text-xs text-gray-400 font-medium">
            {`${hour.toString().padStart(2, '0')}:00`}
          </span>
        </div>
      ))}
    </div>
  );

  // Render day column with events
  const renderDayColumn = (date: Date, isOnly: boolean = false) => {
    const dayEvents = getEventsForDay(date);
    const isCurrentDay = isToday(date);
    
    return (
      <div 
        className={`flex-1 relative border-r border-gray-200 ${isCurrentDay ? 'bg-blue-50/30' : ''}`}
        style={{ minWidth: isOnly ? 'auto' : '120px' }}
      >
        {/* Hour grid lines */}
        {TIME_SLOTS.map(hour => (
          <div 
            key={hour} 
            className="h-[60px] border-b border-gray-100"
          />
        ))}
        
        {/* Events overlay */}
        <div className="absolute inset-0 pointer-events-none">
          {dayEvents.map(event => {
            const { top, height } = getEventStyle(event, date);
            const colors = getEventColors(event);
            const column = event.column || 0;
            const totalColumns = event.totalColumns || 1;
            const width = `calc(${100 / totalColumns}% - 4px)`;
            const left = `calc(${(column / totalColumns) * 100}% + 2px)`;
            
            return (
              <div
                key={event.id}
                onClick={() => { setSelectedEvent(event); setShowEventModal(true); }}
                className={`absolute rounded-md ${colors.bg} ${colors.border} border cursor-pointer pointer-events-auto overflow-hidden shadow-sm hover:shadow-md transition-shadow group`}
                style={{
                  top: `${top}px`,
                  height: `${height}px`,
                  left,
                  width,
                  minHeight: '22px'
                }}
              >
                {/* Accent bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${colors.accent}`} />
                
                {/* Content */}
                <div className="pl-2.5 pr-1.5 py-1 h-full flex flex-col">
                  <div className={`text-xs font-semibold ${colors.text} truncate flex items-center gap-1`}>
                    {event.is_online_meeting && (
                      <Video className="w-3 h-3 flex-shrink-0" />
                    )}
                    <span className="truncate">{event.subject}</span>
                  </div>
                  
                  {height > 40 && (
                    <div className={`text-[10px] ${colors.text} opacity-75 mt-0.5`}>
                      {format(parseISO(event.start.dateTime), 'HH:mm')} - {format(parseISO(event.end.dateTime), 'HH:mm')}
                    </div>
                  )}
                  
                  {height > 60 && event.location && (
                    <div className={`text-[10px] ${colors.text} opacity-60 truncate mt-0.5 flex items-center gap-1`}>
                      <MapPin className="w-2.5 h-2.5" />
                      {event.location}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Current time indicator */}
        {isCurrentDay && (
          <CurrentTimeIndicator />
        )}
      </div>
    );
  };

  // Current time indicator
  const CurrentTimeIndicator = () => {
    const now = new Date();
    const minutes = (getHours(now) - START_HOUR) * 60 + getMinutes(now);
    
    if (minutes < 0 || minutes > (END_HOUR - START_HOUR) * 60) return null;
    
    const top = (minutes / 60) * HOUR_HEIGHT;
    
    return (
      <div 
        className="absolute left-0 right-0 z-20 pointer-events-none"
        style={{ top: `${top}px` }}
      >
        <div className="flex items-center">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500 -ml-1" />
          <div className="flex-1 h-0.5 bg-red-500" />
        </div>
      </div>
    );
  };

  // Render day view
  const renderDayView = () => (
    <div className="flex-1 flex overflow-auto">
      {renderTimeColumn()}
      {renderDayColumn(currentDate, true)}
    </div>
  );

  // Render week view
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Day headers */}
        <div className="flex border-b border-gray-200 bg-white sticky top-0 z-10">
          <div className="w-16 flex-shrink-0 border-r border-gray-200" />
          {days.map(day => (
            <div 
              key={day.toISOString()} 
              className={`flex-1 text-center py-3 border-r border-gray-200 min-w-[120px] ${
                isToday(day) ? 'bg-blue-50' : ''
              }`}
            >
              <div className="text-xs text-gray-500 uppercase font-medium">
                {format(day, 'EEE', { locale: dateLocale })}
              </div>
              <div className={`text-xl font-semibold mt-0.5 ${
                isToday(day) 
                  ? 'w-8 h-8 mx-auto rounded-full bg-blue-600 text-white flex items-center justify-center' 
                  : 'text-gray-800'
              }`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
        
        {/* Scrollable time grid */}
        <div className="flex-1 flex overflow-auto">
          {renderTimeColumn()}
          <div className="flex-1 flex">
            {days.map(day => (
              <React.Fragment key={day.toISOString()}>
                {renderDayColumn(day)}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render month view
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    let day = calendarStart;
    
    while (day <= calendarEnd) {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      day = addDays(day, 1);
    }
    
    return (
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(d => (
            <div key={d} className="text-center py-2 text-xs font-semibold text-gray-600 uppercase">
              {d}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="flex-1 grid" style={{ gridTemplateRows: `repeat(${weeks.length}, 1fr)` }}>
          {weeks.map((week, weekIdx) => (
            <div key={weekIdx} className="grid grid-cols-7 border-b border-gray-200">
              {week.map(day => {
                const dayEvents = events.filter(e => isSameDay(parseISO(e.start.dateTime), day));
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isCurrentDay = isToday(day);
                
                return (
                  <div 
                    key={day.toISOString()} 
                    className={`min-h-[100px] border-r border-gray-200 p-1 ${
                      isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                    } ${isCurrentDay ? 'ring-2 ring-inset ring-blue-500' : ''}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      isCurrentDay 
                        ? 'w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs' 
                        : isCurrentMonth ? 'text-gray-800' : 'text-gray-400'
                    }`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map(event => {
                        const colors = getEventColors(event);
                        return (
                          <div
                            key={event.id}
                            onClick={() => { setSelectedEvent(event); setShowEventModal(true); }}
                            className={`${colors.bg} ${colors.border} border-l-2 ${colors.text} text-xs px-1 py-0.5 rounded-r cursor-pointer hover:shadow-sm truncate`}
                          >
                            {event.is_online_meeting && <Video className="w-3 h-3 inline mr-1" />}
                            {format(parseISO(event.start.dateTime), 'HH:mm')} {event.subject}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500 px-1">
                          +{dayEvents.length - 3} mer
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-gray-900">{t.title}</h1>
          
          {/* Navigation */}
          <div className="flex items-center gap-1">
            <button 
              onClick={goBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t.today}
            </button>
            <button 
              onClick={goForward}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          <span className="text-lg font-semibold text-gray-800 capitalize">
            {getViewTitle()}
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Outlook status */}
          {outlookConnected ? (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">
              <Check className="w-4 h-4" />
              {t.outlookConnected}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              {t.outlookNotConnected}
            </div>
          )}
          
          {/* Sync button */}
          <button
            onClick={syncOutlook}
            disabled={syncing}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title={t.sync}
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${syncing ? 'animate-spin' : ''}`} />
          </button>
          
          {/* View toggle */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setView('day')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                view === 'day' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.day}
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 ${
                view === 'week' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.week}
            </button>
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-200 ${
                view === 'month' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.month}
            </button>
          </div>
          
          {/* Create event button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t.newEvent}
          </button>
        </div>
      </div>
      
      {/* Calendar content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {view === 'day' && renderDayView()}
          {view === 'week' && renderWeekView()}
          {view === 'month' && renderMonthView()}
        </>
      )}
      
      {/* Event details modal - World Class Design */}
      {showEventModal && selectedEvent && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowEventModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient */}
            <div className={`p-6 ${
              selectedEvent.subject?.toLowerCase().includes('cancelled') 
                ? 'bg-gradient-to-r from-gray-600 to-gray-700' 
                : selectedEvent.is_online_meeting 
                  ? 'bg-gradient-to-r from-violet-600 to-purple-700'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  <div className="flex items-center gap-2 mb-2">
                    {selectedEvent.is_online_meeting && (
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-white/20 rounded-full">
                        <Video className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs font-medium text-white">Teams-møte</span>
                      </div>
                    )}
                    {selectedEvent.subject?.toLowerCase().includes('cancelled') && (
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-white/20 rounded-full">
                        <X className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs font-medium text-white">Avlyst</span>
                      </div>
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-white leading-tight">
                    {selectedEvent.subject?.replace('Cancelled: ', '')}
                  </h2>
                  {selectedEvent.organizer && (
                    <p className="text-sm text-white/80 mt-1">
                      Organisert av {selectedEvent.organizer.name || selectedEvent.organizer.address}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Date & Time - Primary info card */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex flex-col items-center justify-center border border-gray-200">
                  <span className="text-xs font-semibold text-gray-500 uppercase">
                    {format(parseISO(selectedEvent.start.dateTime), 'MMM', { locale: dateLocale })}
                  </span>
                  <span className="text-xl font-bold text-gray-800">
                    {format(parseISO(selectedEvent.start.dateTime), 'd')}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                    {format(parseISO(selectedEvent.start.dateTime), 'EEEE', { locale: dateLocale })}
                  </div>
                  <div className="text-gray-600 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {format(parseISO(selectedEvent.start.dateTime), 'HH:mm')} – {format(parseISO(selectedEvent.end.dateTime), 'HH:mm')}
                    <span className="text-gray-400">
                      ({differenceInMinutes(parseISO(selectedEvent.end.dateTime), parseISO(selectedEvent.start.dateTime))} min)
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Teams Meeting Button */}
              {(selectedEvent.is_online_meeting || selectedEvent.online_meeting_url || selectedEvent.teams_meeting?.joinUrl) && (
                <a
                  href={selectedEvent.online_meeting_url || selectedEvent.teams_meeting?.joinUrl || extractTeamsLink(selectedEvent.body)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-xl transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-violet-600 flex items-center justify-center">
                      <Video className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-violet-900">Bli med i Teams-møte</div>
                      <div className="text-sm text-violet-600">Klikk for å åpne Microsoft Teams</div>
                    </div>
                  </div>
                  <ExternalLink className="w-5 h-5 text-violet-400 group-hover:text-violet-600 transition-colors" />
                </a>
              )}
              
              {/* Location */}
              {selectedEvent.location && !selectedEvent.location.includes('Microsoft Teams') && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-700">{selectedEvent.location}</span>
                </div>
              )}
              
              {/* Attendees */}
              {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Deltakere ({selectedEvent.attendees.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedEvent.attendees.map((att, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-sm font-semibold text-gray-600">
                              {(att.name || att.email)?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-800 text-sm">
                              {att.name || att.email}
                            </div>
                            {att.name && att.email && att.name !== att.email && (
                              <div className="text-xs text-gray-500">{att.email}</div>
                            )}
                          </div>
                        </div>
                        {att.status && (
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            att.status === 'accepted' || att.status === 'Accepted'
                              ? 'bg-green-100 text-green-700' 
                              : att.status === 'declined' || att.status === 'Declined'
                                ? 'bg-red-100 text-red-700'
                                : att.status === 'tentative' || att.status === 'Tentative'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-gray-100 text-gray-600'
                          }`}>
                            {att.status === 'accepted' || att.status === 'Accepted' ? (
                              <><Check className="w-3 h-3" /> Akseptert</>
                            ) : att.status === 'declined' || att.status === 'Declined' ? (
                              <><X className="w-3 h-3" /> Avslått</>
                            ) : att.status === 'tentative' || att.status === 'Tentative' ? (
                              <><Clock className="w-3 h-3" /> Foreløpig</>
                            ) : (
                              <><Clock className="w-3 h-3" /> Venter</>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Meeting info from body - Parse Teams info nicely */}
              {selectedEvent.body && extractMeetingInfo(selectedEvent.body) && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Møteinformasjon
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    {extractMeetingInfo(selectedEvent.body)?.meetingId && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Møte-ID:</span>
                        <code className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200">
                          {extractMeetingInfo(selectedEvent.body)?.meetingId}
                        </code>
                      </div>
                    )}
                    {extractMeetingInfo(selectedEvent.body)?.passcode && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Passord:</span>
                        <code className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200">
                          {extractMeetingInfo(selectedEvent.body)?.passcode}
                        </code>
                      </div>
                    )}
                    {extractMeetingInfo(selectedEvent.body)?.dialIn && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Ring inn:</span>
                        <a 
                          href={`tel:${extractMeetingInfo(selectedEvent.body)?.dialIn?.replace(/\s/g, '')}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {extractMeetingInfo(selectedEvent.body)?.dialIn}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Case link */}
              {selectedEvent.case_title && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CalendarIcon className="w-5 h-5 text-blue-500" />
                  <span className="text-blue-700">Tilknyttet sak: {selectedEvent.case_title}</span>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-500">
                {selectedEvent.organizer && `Organisert av ${selectedEvent.organizer.name || selectedEvent.organizer.address}`}
              </div>
              <div className="flex gap-2">
                {selectedEvent.case_id && onNavigateToCase && (
                  <button
                    onClick={() => { onNavigateToCase(selectedEvent.case_id!); setShowEventModal(false); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Gå til sak
                  </button>
                )}
                <button
                  onClick={() => setShowEventModal(false)}
                  className="px-4 py-2 text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Lukk
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Create event modal - Minimalist Design */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
            {/* Header - Clean minimal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">{t.createEvent}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t.eventTitle} *</label>
                <input
                  type="text"
                  value={newEvent.subject}
                  onChange={(e) => setNewEvent({ ...newEvent, subject: e.target.value })}
                  placeholder={t.eventTitlePlaceholder}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm"
                />
              </div>
              
              {/* Date and time - Compact grid */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t.date}</label>
                  <input
                    type="date"
                    value={newEvent.startDate}
                    onChange={(e) => setNewEvent({ ...newEvent, startDate: e.target.value, endDate: e.target.value })}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t.startTime}</label>
                  <input
                    type="time"
                    value={newEvent.startTime}
                    onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t.date}</label>
                  <input
                    type="date"
                    value={newEvent.endDate}
                    onChange={(e) => setNewEvent({ ...newEvent, endDate: e.target.value })}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t.endTime}</label>
                  <input
                    type="time"
                    value={newEvent.endTime}
                    onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              
              {/* Location */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t.location}</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  placeholder={t.locationPlaceholder}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm placeholder:text-gray-400"
                />
              </div>
              
              {/* Teams meeting toggle - Minimal style */}
              <button 
                type="button"
                onClick={() => setNewEvent({ ...newEvent, isTeamsMeeting: !newEvent.isTeamsMeeting })}
                className={`w-full flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${
                  newEvent.isTeamsMeeting 
                    ? 'bg-violet-50 ring-1 ring-violet-200' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    newEvent.isTeamsMeeting ? 'bg-violet-500' : 'bg-gray-300'
                  }`}>
                    <Video className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-left">
                    <div className={`text-sm font-medium ${
                      newEvent.isTeamsMeeting ? 'text-violet-900' : 'text-gray-700'
                    }`}>
                      {t.teamsMeeting}
                    </div>
                    <div className={`text-xs ${
                      newEvent.isTeamsMeeting ? 'text-violet-600' : 'text-gray-400'
                    }`}>
                      Auto-generate Teams link
                    </div>
                  </div>
                </div>
                {/* Toggle switch with inline styles for reliable rendering */}
                <div 
                  className="flex-shrink-0"
                  style={{
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    backgroundColor: newEvent.isTeamsMeeting ? '#8b5cf6' : '#d1d5db',
                    position: 'relative',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div 
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '10px',
                      backgroundColor: 'white',
                      position: 'absolute',
                      top: '2px',
                      left: newEvent.isTeamsMeeting ? '22px' : '2px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left 0.2s'
                    }}
                  />
                </div>
              </button>
              
              {/* Attendees */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Attendees</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={attendeeInput}
                    onChange={(e) => setAttendeeInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAttendee())}
                    placeholder="email@example.com"
                    className="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm placeholder:text-gray-400"
                  />
                  <button
                    onClick={addAttendee}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
                {newEvent.attendees.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {newEvent.attendees.map((att, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full text-xs"
                      >
                        <span className="text-gray-700">{att.email}</span>
                        <button
                          onClick={() => removeAttendee(att.email)}
                          className="p-0.5 hover:bg-gray-200 rounded-full"
                        >
                          <X className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t.description}</label>
                <textarea
                  value={newEvent.body}
                  onChange={(e) => setNewEvent({ ...newEvent, body: e.target.value })}
                  rows={3}
                  placeholder={t.descriptionPlaceholder}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none text-sm placeholder:text-gray-400"
                />
              </div>
            </div>
            
            {/* Footer - Clean buttons */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t.cancel}
              </button>
              <button
                onClick={createEvent}
                disabled={creating || !newEvent.subject.trim()}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  newEvent.isTeamsMeeting 
                    ? 'bg-violet-600 hover:bg-violet-700' 
                    : 'bg-gray-900 hover:bg-gray-800'
                }`}
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.creating}
                  </>
                ) : (
                  <>
                    {newEvent.isTeamsMeeting ? <Video className="w-4 h-4" /> : <CalendarIcon className="w-4 h-4" />}
                    {t.create}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutlookCalendar;
